import React, {useEffect, useRef, useState, useCallback} from 'react';
import {Box, CircularProgress, Stack, styled} from '@mui/material';
import {Body, designSystemToken, Icon} from '@lightricks/react-design-system';
import {IconProps} from '@lightricks/react-design-system/dist/components/icon/IconProps';

const DEFAULT_WIDTH = 160;
const DEFAULT_HEIGHT = 190;
const ICON_SIZE_MAP = {
  small: 24,
  medium: 32,
  large: 40,
};
const HOVER_THRESHOLD = 50;

const LOADER_SIZE_MAP = {
  small: 16,
  medium: 24,
  large: 32,
};

// Global video manager to prevent multiple videos playing
let activeVideoRef: HTMLVideoElement | null = null;

const shouldNotForwardProps = ['iconSize', 'isSelected', 'positionCorrection'];

const IconContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({iconSize}: {iconSize: IconProps['size']}) => ({
  position: 'absolute',
  bottom: '34px',
  left: '12px',
  zIndex: 1,
  width: ICON_SIZE_MAP[iconSize || 'medium'], // Add some padding around the icon
  height: ICON_SIZE_MAP[iconSize || 'medium'],
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: designSystemToken('semantic.bg.tertiary'),
}));

const AssetContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{
  isSelected: boolean;
  width: number;
  height: number;
}>(({isSelected, width, height}) => ({
  border: isSelected
    ? `2px solid ${designSystemToken('semantic.bg.neutral-inverse')}`
    : '2px solid transparent',
  boxShadow: isSelected
    ? `0px 0px 0px 3px ${designSystemToken('semantic.fg.primary')}`
    : '0px 0px 0px 3px transparent',
  borderRadius: '8px',
  width,
  height,
  boxSizing: 'border-box',
  cursor: 'pointer',
  overflow: 'hidden',
}));

const VideoElement = styled('video', {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{positionCorrection?: string}>(({positionCorrection}) => ({
  boxSizing: 'border-box',
  objectFit: 'cover',
  borderRadius: '8px',
  objectPosition: positionCorrection,
}));

const ImageElement = styled('img', {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{positionCorrection?: string}>(({positionCorrection}) => ({
  boxSizing: 'border-box',
  objectFit: 'cover',
  borderRadius: '8px',
  objectPosition: positionCorrection,
}));

function SampleAssetDisplay({
  isSelected,
  setSelectedAsset,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  iconSize = 'small',
  overrideMuted = true,
  onMuteChange,
  asset,
  preloadVideo = true,
}: {
  isSelected: boolean;
  setSelectedAsset: () => void;
  width?: number;
  height?: number;
  iconSize?: IconProps['size'];
  overrideMuted?: boolean;
  onMuteChange?: (muted: boolean) => void;
  preloadVideo?: boolean;
  asset: {
    id: number | string;
    title: string;
    image: string;
    video: string;
    positionCorrection?: string | undefined;
  };
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(overrideMuted);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(preloadVideo);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setIsMuted(overrideMuted);
  }, [overrideMuted]);

  const handleMutedChanged = (muted: boolean) => {
    setIsMuted(muted);
    onMuteChange?.(muted);
  };

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  const videoRef = useRef<HTMLVideoElement>(null);

  // Handle hover to load video with delay
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);

    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Set a delay before loading video
    hoverTimeoutRef.current = setTimeout(() => {
      if (asset.video && !shouldLoadVideo) {
        setShouldLoadVideo(true);
      }
    }, HOVER_THRESHOLD);
  }, [asset.video, shouldLoadVideo]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);

    // Clear timeout to prevent video loading if hover is brief
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!videoRef.current || !isVideoLoaded) {
      return;
    }

    if (isHovered) {
      // Pause any currently playing video
      if (activeVideoRef && activeVideoRef !== videoRef.current) {
        activeVideoRef.pause();
        activeVideoRef.currentTime = 0;
      }

      // Set this as the active video and play
      activeVideoRef = videoRef.current;
      videoRef.current.play();
    } else {
      // Pause this video if it's the active one
      // eslint-disable-next-line no-lonely-if
      if (activeVideoRef === videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
        activeVideoRef = null;
      }
    }
  }, [isHovered, isVideoLoaded]);

  // Cleanup video element when component unmounts
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
        videoRef.current.load();

        // Clear global reference if this was the active video
        if (activeVideoRef === videoRef.current) {
          activeVideoRef = null;
        }
      }

      // Clear any pending hover timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Reset video state when asset changes
  useEffect(() => {
    setShouldLoadVideo(false);
    setIsVideoLoaded(false);
    setIsHovered(false);
  }, [asset.id]);

  return (
    <Stack direction="column" alignItems="center" gap="4px" position="relative">
      <AssetContainer
        isSelected={isSelected}
        width={width}
        height={height}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={setSelectedAsset}
      >
        {shouldLoadVideo ? (
          <VideoElement
            src={asset.video}
            poster={asset.image}
            loop
            muted={isMuted}
            width="100%"
            height="100%"
            positionCorrection={asset.positionCorrection}
            onLoadedData={handleVideoLoad}
            ref={videoRef}
          />
        ) : (
          <ImageElement
            src={asset.image}
            width="100%"
            height="100%"
            positionCorrection={asset.positionCorrection}
          />
        )}
        {isHovered && (
          <IconContainer
            iconSize={iconSize}
            onClick={(event) => {
              event.stopPropagation();
              handleMutedChanged(!isMuted);
            }}
          >
            {isVideoLoaded ? (
              <Icon
                name={isMuted ? 'Features-Audio-Mute' : 'Features-Audio-On'}
                size={iconSize}
                appearance="primary"
              />
            ) : (
              <CircularProgress size={LOADER_SIZE_MAP[iconSize]} />
            )}
          </IconContainer>
        )}
      </AssetContainer>
      <Body
        size="md"
        alignSelf="flex-start"
        color={
          isSelected
            ? designSystemToken('semantic.fg.primary')
            : designSystemToken('semantic.fg.secondary')
        }
      >
        {asset.title}
      </Body>
    </Stack>
  );
}

export default SampleAssetDisplay;
